import type {
  AdminAsset,
  AdminShootSection,
  UpdateShootRequest,
  VersionedShoot,
} from '@diana/contracts';
import {
  galleryDescriptionText,
  galleryDocumentTitle,
  galleryMetaDescription,
} from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { CompactRichTextField } from '../../components/CompactRichTextField.js';
import { ConfirmedSlugField } from '../../components/ConfirmedSlugField.js';
import { ConflictDialog } from '../../components/ConflictDialog.js';
import { readRevisionConflict } from '../../lib/use-conflict.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { useDianaUIPortal } from '../../ui/provider/DianaUIProvider.js';
import { Button, Checkbox, Toggle } from '../../ui/index.js';
import { ContextualGalleryField, ContextualMediaField } from '../media/ContextualMediaField.js';
import { EditorHeading, SelectField, TextField } from '../settings/SettingsEditor.js';

import './ShootEditor.css';

type ShootDraft = Omit<UpdateShootRequest, 'expectedRevision'>;
type EditorTab = 'overview' | 'photos' | 'seo';

export function ShootEditor({
  shoot,
  assets,
  save,
  remove,
}: {
  shoot: VersionedShoot;
  assets: readonly AdminAsset[];
  save(request: UpdateShootRequest): Promise<VersionedShoot>;
  remove(expectedRevision: number): Promise<void>;
}) {
  const compact = useCompactGallery();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsTrigger = useRef<HTMLButtonElement | null>(null);
  const portal = useDianaUIPortal();
  const [tab, setTab] = useState<EditorTab>('overview');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const autosave = useAutosave<ShootDraft>({
    initialValue: shootDraft(shoot),
    initialRevision: shoot.revision,
    save: async ({ value, expectedRevision }) => {
      const result = await save({ ...value, expectedRevision });
      return { value: shootDraft(result), revision: result.revision };
    },
    readConflict: (error) =>
      readRevisionConflict(error, (current) => (isShoot(current) ? shootDraft(current) : null)),
  });
  const draft = autosave.snapshot.value;
  const gallery = draft.sections[0] as Extract<AdminShootSection, { type: 'Gallery' }>;
  useEditorCommandRegistration({
    status: autosave.snapshot.status,
    save: () => void autosave.flush(),
  });
  const update = (patch: Partial<ShootDraft>) => autosave.edit({ ...draft, ...patch });
  const updatePayload = (patch: Partial<ShootDraft['payload']>) =>
    update({ payload: { ...draft.payload, ...patch } });
  const updateGallery = (
    items: Extract<AdminShootSection, { type: 'Gallery' }>['payload']['items'],
  ) => update({ sections: [{ ...gallery, payload: { layout: 'EditorialGrid', items } }] });

  const detailFields = (
    <div className="gallery-editor__detail-fields">
      <SelectField
        label="Portfolio category"
        value={draft.payload.category ?? 'portraits'}
        options={[
          ...new Set([
            '',
            'portraits',
            'families',
            'proposals',
            'weddings',
            ...(draft.payload.category ? [draft.payload.category] : []),
          ]),
        ]}
        onChange={(category) => updatePayload({ category: category || undefined })}
      />
      <TextField
        label="Date"
        type="date"
        value={dateInputValue(draft.payload.dateLabel)}
        onChange={(dateLabel) => updatePayload({ dateLabel: dateLabel || undefined })}
      />
      <ConfirmedSlugField
        label="URL slug"
        slug={draft.slug}
        routeFor={computedRoute}
        onConfirm={(slug) => update({ slug })}
      />
    </div>
  );

  return (
    <article className="editor gallery-editor">
      <Link className="dui-back-link" to="/shoots">
        <ArrowLeft size={14} aria-hidden="true" /> Back to Galleries
      </Link>
      <div className="gallery-editor__band">
        <EditorHeading
          title={draft.payload.title}
          status={autosave.snapshot.status}
          onRetry={() => void autosave.retry()}
        />
        <p className="gallery-editor__metadata">
          {draft.payload.category || 'Uncategorised'} · {draft.payload.dateLabel || 'No date'} ·{' '}
          {gallery.payload.items.length} photos · {computedRoute(draft.slug)}
        </p>
        <div className="gallery-editor__visibility">
          <div>
            <strong>
              {draft.siteAccess === 'Public' ? 'Include in Portfolio' : 'Hidden from Portfolio'}
            </strong>
            <p>Publish to update the site.</p>
          </div>
          <Toggle
            label="Public"
            hideLabel
            checked={draft.siteAccess === 'Public'}
            onCheckedChange={(checked) => update({ siteAccess: checked ? 'Public' : 'AdminOnly' })}
          />
        </div>
        <nav className="gallery-editor__tabs" aria-label="Gallery editor">
          {(['overview', 'photos', 'seo'] as const).map((value) => (
            <button
              type="button"
              key={value}
              aria-current={tab === value ? 'page' : undefined}
              onClick={() => setTab(value)}
            >
              {value[0]!.toUpperCase() + value.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' ? (
        <section className="form-section form-section--flush gallery-editor__panel">
          <div className="gallery-editor__overview-layout">
            <div className="gallery-editor__overview-cover">
              <ContextualMediaField
                title="Gallery cover"
                profile="wide"
                cropProfile="portrait-3-4"
                value={draft.payload.cover}
                assets={assets}
                onChange={(cover) => updatePayload({ cover })}
              />
              <p className="inline-note">
                Shown as 3:4 in Portfolio. Replace the image to adjust its crop.
              </p>
            </div>
            <div className="gallery-editor__overview-fields">
              <TextField
                label="Gallery title"
                value={draft.payload.title}
                onChange={(title) => updatePayload({ title })}
              />
              <TextField
                label="Subtitle"
                value={draft.payload.subtitle ?? ''}
                optional
                onChange={(subtitle) => updatePayload({ subtitle: subtitle || undefined })}
              />
              {compact ? (
                <Dialog.Root open={detailsOpen} onOpenChange={setDetailsOpen}>
                  <div className="gallery-editor__detail-rows">
                    {[
                      ['Category', draft.payload.category || 'Uncategorised'],
                      ['Date', draft.payload.dateLabel || 'No date'],
                      ['Address', computedRoute(draft.slug)],
                    ].map(([label, value]) => (
                      <Dialog.Trigger key={label} asChild>
                        <button
                          type="button"
                          aria-label={`Edit ${label?.toLowerCase()}`}
                          onClick={(event) => {
                            detailsTrigger.current = event.currentTarget;
                          }}
                        >
                          <span>{label}</span>
                          <strong>{value}</strong>
                          <ChevronRight size={16} aria-hidden="true" />
                        </button>
                      </Dialog.Trigger>
                    ))}
                  </div>
                  <Dialog.Portal container={portal}>
                    <Dialog.Overlay className="gallery-sheet-overlay" />
                    <Dialog.Content
                      className="gallery-sheet gallery-details-sheet"
                      onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        detailsTrigger.current?.focus();
                      }}
                    >
                      <Dialog.Title>Gallery details</Dialog.Title>
                      <Dialog.Description>
                        Change the category, date or confirmed gallery address.
                      </Dialog.Description>
                      {detailFields}
                      <Dialog.Close asChild>
                        <Button variant="primary">Done</Button>
                      </Dialog.Close>
                    </Dialog.Content>
                  </Dialog.Portal>
                </Dialog.Root>
              ) : (
                detailFields
              )}
              <CompactRichTextField
                label="Gallery description"
                value={draft.payload.description ?? plainRichText(draft.payload.seo.description)}
                onChange={(description) => updatePayload({ description })}
              />
            </div>
          </div>
          <div className="gallery-editor__danger">
            <div>
              <strong>Delete gallery</strong>
              <p>
                Permanently removes this Gallery, its server-managed photographs and all Mac sync
                data. The original Mac folder is untouched.
              </p>
            </div>
            <Dialog.Root
              open={confirmingDelete}
              onOpenChange={(open) => {
                if (!deleting) {
                  setConfirmingDelete(open);
                  if (open) setDeleteError('');
                }
              }}
            >
              <Dialog.Trigger asChild>
                <Button
                  variant="destructive"
                  leadingIcon={Trash2}
                  disabled={autosave.snapshot.status !== 'Saved'}
                >
                  Delete gallery
                </Button>
              </Dialog.Trigger>
              <Dialog.Portal container={portal}>
                <Dialog.Overlay className="gallery-sheet-overlay" />
                <Dialog.Content
                  role="alertdialog"
                  className="gallery-sheet gallery-delete-dialog"
                  onEscapeKeyDown={(event) => {
                    if (deleting) event.preventDefault();
                  }}
                  onInteractOutside={(event) => event.preventDefault()}
                >
                  <p className="eyebrow">Permanent action</p>
                  <Dialog.Title>Delete “{draft.payload.title}”?</Dialog.Title>
                  <Dialog.Description>
                    The gallery and its {gallery.payload.items.length} photographs will be removed.
                    Server-managed files and generated descriptions linked only to this gallery,
                    plus all Mac sync data, will be permanently deleted. The original Mac folder is
                    untouched. Files on Diana’s Mac will not be changed.
                  </Dialog.Description>
                  {deleteError ? (
                    <p className="field-error" role="alert">
                      {deleteError}
                    </p>
                  ) : null}
                  <div className="gallery-sheet__actions">
                    <Dialog.Close asChild>
                      <Button variant="secondary" disabled={deleting}>
                        Cancel
                      </Button>
                    </Dialog.Close>
                    <Button
                      variant="destructive"
                      loading={deleting}
                      disabled={deleting || autosave.snapshot.status !== 'Saved'}
                      onClick={() => void confirmDelete()}
                    >
                      Delete gallery and files
                    </Button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
        </section>
      ) : null}

      {tab === 'photos' ? (
        <section className="form-section form-section--flush gallery-editor__panel">
          <ContextualGalleryField
            title="Gallery photographs"
            description="Usually uploaded from the Mac app. Select a photograph to edit its alt text, change the order or remove it. The cover is shown first in this editor."
            profile="standard"
            items={gallery.payload.items}
            assets={assets}
            selectionEditor={{ compact }}
            createItem={(media) => ({ media })}
            onChange={updateGallery}
          />
        </section>
      ) : null}

      {tab === 'seo' ? (
        <section className="form-section form-section--flush gallery-editor__panel gallery-editor__seo">
          <div>
            <h2>How it looks in Google</h2>
            <section className="gallery-editor__search-preview" aria-label="Google search preview">
              <span>perova.photo › portfolio › {draft.slug}</span>
              <div className="field">
                <span className="sr-only">SEO title</span>
                <p data-testid="derived-gallery-title">
                  {galleryDocumentTitle(draft.payload.title)}
                </p>
              </div>
              <div className="field">
                <span className="sr-only">SEO description</span>
                <p data-testid="derived-gallery-description">
                  {galleryMetaDescription(
                    galleryDescriptionText(
                      draft.payload.description,
                      draft.payload.seo.description,
                    ),
                  )}
                </p>
              </div>
            </section>
            <p className="inline-note">
              Made automatically from the gallery title and description. Edit them in Overview to
              change this preview. Search engines may display different text.
            </p>
            <div className="home-choice">
              <Checkbox
                label="Prevent search indexing"
                description="When published, excludes this gallery from search indexing and the sitemap."
                checked={draft.payload.seo.noindex}
                onChange={(event) =>
                  updatePayload({
                    seo: { ...draft.payload.seo, noindex: event.currentTarget.checked },
                  })
                }
              />
            </div>
            <p className="inline-note">Canonical route: {computedRoute(draft.slug)}</p>
          </div>
          <div>
            <ContextualMediaField
              title="Social sharing image"
              profile="wide"
              cropProfile="social"
              description="Optional override for Open Graph and social previews."
              value={draft.payload.seo.socialImage}
              assets={assets}
              onChange={(socialImage) =>
                updatePayload({ seo: { ...draft.payload.seo, socialImage } })
              }
            />
            <p className="inline-note">
              Optional override for sharing previews. Without an override, the gallery cover is
              used.
            </p>
          </div>
        </section>
      ) : null}
      <ConflictDialog
        open={autosave.snapshot.status === 'Conflict'}
        onAcceptServer={autosave.acceptServerVersion}
        onReapplyLocal={autosave.reapplyLocal}
        onCopyLocal={() =>
          void navigator.clipboard?.writeText(JSON.stringify(autosave.copyLocal(), null, 2))
        }
      />
    </article>
  );

  async function confirmDelete() {
    if (deleting || autosave.snapshot.status !== 'Saved') return;
    setDeleting(true);
    setDeleteError('');
    try {
      await remove(autosave.snapshot.revision);
    } catch (error) {
      setDeleteError(deleteErrorMessage(error));
      setDeleting(false);
    }
  }
}

function useCompactGallery() {
  const [compact, setCompact] = useState(
    () => window.matchMedia?.('(max-width: 700px)').matches ?? false,
  );
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 700px)');
    if (!media) return;
    const change = () => setCompact(media.matches);
    change();
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  return compact;
}

function deleteErrorMessage(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'fields' in error &&
    Array.isArray(error.fields) &&
    typeof error.fields[0]?.message === 'string'
  )
    return error.fields[0].message;
  return error instanceof Error ? error.message : 'Could not delete the gallery';
}

function shootDraft(shoot: VersionedShoot): ShootDraft {
  const gallery = flattenGallery(shoot);
  return {
    slug: shoot.slug,
    siteAccess: shoot.siteAccess,
    payload: shoot.payload,
    sections: [gallery],
  };
}

function flattenGallery(shoot: VersionedShoot): Extract<AdminShootSection, { type: 'Gallery' }> {
  const gallery = shoot.sections.find(
    (section): section is Extract<AdminShootSection, { type: 'Gallery' }> =>
      section.type === 'Gallery',
  );
  const items = new Map<
    string,
    Extract<AdminShootSection, { type: 'Gallery' }>['payload']['items'][number]
  >();
  items.set(shoot.payload.cover.assetId, { media: shoot.payload.cover });
  for (const section of shoot.sections) {
    if (section.type === 'Gallery') {
      for (const item of section.payload.items) items.set(item.media.assetId, item);
    } else if (section.type === 'Feature' && section.payload.media) {
      items.set(section.payload.media.assetId, { media: section.payload.media });
    }
  }
  return {
    id: gallery?.id ?? crypto.randomUUID(),
    type: 'Gallery',
    position: 0,
    payload: { layout: 'EditorialGrid', items: [...items.values()] },
  };
}

function computedRoute(slug: string) {
  return `/portfolio/${slug || 'gallery'}`;
}

function dateInputValue(value: string | undefined) {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const monthYear = value.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYear) {
    const months = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december',
    ];
    const month = months.indexOf(monthYear[1]!.toLocaleLowerCase());
    if (month >= 0) return `${monthYear[2]}-${String(month + 1).padStart(2, '0')}-01`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, '0'),
    String(parsed.getDate()).padStart(2, '0'),
  ].join('-');
}

function plainRichText(text: string) {
  return {
    type: 'document' as const,
    children: [{ type: 'paragraph' as const, children: [{ type: 'text' as const, text }] }],
  };
}

function isShoot(value: unknown): value is VersionedShoot {
  return !!value && typeof value === 'object' && 'sections' in value && 'route' in value;
}
